// ./scenes/CommunityAngelFund.jsx
import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Button,
    CircularProgress,
} from '@mui/material';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import WingGraphic from '../../assets/wing.svg';

const API_BASE = 'https://figazuresql.swancreate.com/api/swa';
const IMAGE_BASE = 'https://media.swancreate.com/images/figfinance';
const PAYMENT_PAGE = 'https://nbfinance.figk-12.com/paymentcollectionpage';
const unwrap = j => (Array.isArray(j) ? j : j.value || []);

export default function CommunityAngelFund() {
    /* ────────────────────────────────────────────────────────── */
    /*  1. Force this page into light-mode, restore on unmount   */
    /* ────────────────────────────────────────────────────────── */
    useEffect(() => {
        const prevScheme =
            document.documentElement.getAttribute('data-mui-color-scheme');

        document.documentElement.setAttribute('data-mui-color-scheme', 'light');
        document.body.style.background = '#ffffff';
        document.body.style.colorScheme = 'light';

        return () => {
            if (prevScheme) {
                document.documentElement.setAttribute(
                    'data-mui-color-scheme',
                    prevScheme
                );
            } else {
                document.documentElement.removeAttribute('data-mui-color-scheme');
            }
        };
    }, []);

    /* ────────────────────────────────────────────────────────── */
    /*  2. Inject Google font + page-scoped CSS (once)           */
    /* ────────────────────────────────────────────────────────── */
    useEffect(() => {
        /* Font */
        if (!document.getElementById('poppins-font')) {
            const link = document.createElement('link');
            link.id = 'poppins-font';
            link.rel = 'stylesheet';
            link.href =
                'https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap';
            document.head.appendChild(link);
        }

        /* Styles */
        if (!document.getElementById('caf-styles')) {
            const style = document.createElement('style');
            style.id = 'caf-styles';
            style.innerHTML = `
        :root {
          --caf-red:  #ff6b6b;
          --caf-blue: #1e90ff;
          --caf-bg:   #ffffff;
          --caf-text: #312e2e;
          color-scheme: light;
        }

        body { background:#ffffff; color:var(--caf-text); }

        .caf-section   { padding-inline: clamp(1rem, 5vw, 4rem); }
        .caf-h2        { font: 700 clamp(1.75rem,4vw,2.75rem)/1.2 "Poppins",sans-serif; color:var(--caf-text); }
        .caf-underline { width:120px; height:4px; border-radius:2px; margin:.5rem auto 2rem; }

        .caf-card {
          background: var(--caf-bg);
          border-radius: 20px;
          box-shadow: 0 6px 30px rgba(0,0,0,.1);
          transition: transform .35s, box-shadow .35s;
        }
        .caf-card:hover { transform:translateY(-6px); box-shadow:0 16px 45px rgba(0,0,0,.15); }

        .caf-btn-red {
          background:linear-gradient(90deg,#ff5858 0%,#f857a6 100%);
          color:#fff; text-transform:none; font-weight:700; border-radius:8px;
        }
        .caf-btn-red:hover { background:linear-gradient(90deg,#f857a6 0%,#ff5858 100%); }

        .caf-btn-blue {
          background:linear-gradient(90deg,#1e90ff 0%,#6aa8ff 100%);
          color:#fff; text-transform:none; font-weight:700; border-radius:8px;
        }
        .caf-btn-blue:hover { background:linear-gradient(90deg,#6aa8ff 0%,#1e90ff 100%); }

        @media (min-width:900px){
          .caf-left { text-align:left; margin-inline:auto; max-width:1400px; }
        }
      `;
            document.head.appendChild(style);
        }
    }, []);

    /* ────────────────────────────────────────────────────────── */
    /*  3. Data & state                                          */
    /* ────────────────────────────────────────────────────────── */
    const [sites, setSites] = useState([]);
    const [projects, setProjects] = useState([]);
    const [district, setDistrict] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [dash, mst, fund, dist] = await Promise.all([
                    fetch(`${API_BASE}/vw_StudentDashboard`).then(r => r.json()),
                    fetch(`${API_BASE}/SchoolMst`).then(r => r.json()),
                    fetch(`${API_BASE}/AngelFund`).then(r => r.json()),
                    fetch(`${API_BASE}/SchoolDistrict`).then(r => r.json()),
                ]);
                const dashData = unwrap(dash);
                const mstData = unwrap(mst);
                const fundData = unwrap(fund);
                const distData = unwrap(dist);

                const activeDistrict =
                    distData.find(d => d.isActive) || distData[0] || {};
                setDistrict(activeDistrict);

                const logoMap = mstData.reduce((m, s) => {
                    m[s.apiid] = s.LogoUrl; return m;
                }, {});

                setSites(
                    dashData.map(site => ({
                        ...site,
                        LogoUrl:
                            logoMap[site.SiteID]
                                ? `${IMAGE_BASE}/${logoMap[site.SiteID]}`
                                : activeDistrict.DistrictLogo,
                    }))
                );

                const active = fundData.filter(p => p.isActive);
                setProjects(active.length ? active : fundData);
            } catch (e) {
                console.error('AngelFund load error', e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const money = n =>
        Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

    if (loading) {
        return (
            <Box
                sx={{
                    minHeight: '60vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress size={60} />
            </Box>
        );
    }
    if (!sites.length) {
        return (
            <Box textAlign="center" mt={8}>
                <Typography variant="h6" color="error">
                    No school data available. Check console/network.
                </Typography>
            </Box>
        );
    }

    /* ────────────────────────────────────────────────────────── */
    /*  4. Static mission copy                                   */
    /* ────────────────────────────────────────────────────────── */
    const mission = `Every child deserves the opportunity to enjoy a hot, nutritious meal every single day. When children arrive at school hungry, they face barriers to learning, focusing, and excelling academically. Research consistently shows that children who have adequate nutrition perform better, retain knowledge more effectively, and develop stronger social and cognitive skills.

The Community Angel Fund is dedicated to ensuring no child experiences hunger at school. We rally families, businesses, and neighbors to clear negative lunch account balances, support impactful campus projects, and provide resources that make sure every student has the nutrition needed to thrive.

Join us in our mission to end student hunger and create a brighter, stronger future for all. Your contribution goes beyond today — it shapes the potential leaders, innovators, and citizens who will strengthen our communities tomorrow.`;

    /* ────────────────────────────────────────────────────────── */
    /*  5. Render                                                */
    /* ────────────────────────────────────────────────────────── */
    return (
        <Box sx={{ fontFamily: '"Poppins","Roboto","Arial",sans-serif' }}>
            {/* ░░░ Hero ░░░ */}
            <Box
                sx={{
                    position: 'relative',
                    py: { xs: 6, md: 10 },
                    mb: 10,
                    background: 'linear-gradient(135deg,#ffecd2 0%,#fcb69f 100%)',
                }}
            >
                <Box
                    component="img"
                    src={WingGraphic}
                    alt=""
                    sx={{
                        position: 'absolute',
                        top: '-15%',
                        right: '-15%',
                        width: '70%',
                        opacity: 0.08,
                        transform: 'rotate(15deg)',
                    }}
                />

                <Box className="caf-left" sx={{ px: 4 }}>
                    {district.DistrictLogo && (
                        <Box
                            component="img"
                            src={district.DistrictLogo}
                            alt={district.DistrictName}
                            sx={{ height: { xs: 70, md: 110 }, mb: 3 }}
                        />
                    )}

                    <Typography
                        sx={{
                            font: '700 clamp(2.25rem,5vw,3.5rem)/1.1 "Poppins",sans-serif',
                            color: 'var(--caf-text)',
                        }}
                    >
                        Community Angel Fund
                    </Typography>

                    <Box
                        sx={{
                            width: 80,
                            height: 4,
                            bgcolor: 'var(--caf-red)',
                            borderRadius: 2,
                            mt: 1,
                            mb: 4,
                        }}
                    />

                    <Typography
                        sx={{
                            whiteSpace: 'pre-line',
                            maxWidth: 1000,
                            fontSize: 'clamp(1rem,2.3vw,1.125rem)',
                            lineHeight: 1.8,
                            color: '#333',
                        }}
                    >
                        {mission}
                    </Typography>
                </Box>
            </Box>

            {/* ░░░ Special Projects ░░░ */}
            <Box className="caf-section" sx={{ mb: 12 }}>
                <Typography align="center" className="caf-h2">
                    Special Projects
                </Typography>
                <Box
                    className="caf-underline"
                    sx={{ backgroundColor: 'var(--caf-red)' }}
                />

                <Grid container spacing={6}>
                    {projects.map(p => (
                        <Grid key={p.apiid} item xs={12} md={6} lg={4}>
                            <Card className="caf-card">
                                {p.ImageUrl && (
                                    <CardMedia
                                        component="img"
                                        height="280"
                                        image={p.ImageUrl}
                                        alt={p.ProjectTitle}
                                    />
                                )}

                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 16,
                                        right: 16,
                                        width: 52,
                                        height: 52,
                                        borderRadius: '50%',
                                        bgcolor: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(0,0,0,.1)',
                                    }}
                                >
                                    <VolunteerActivismIcon
                                        sx={{ fontSize: 30, color: 'var(--caf-red)' }}
                                    />
                                </Box>

                                <CardContent sx={{ p: 3 }}>
                                    <Typography
                                        sx={{
                                            font: '700 1.5rem/1.3 "Poppins",sans-serif',
                                            color: 'var(--caf-text)',
                                            mb: 1,
                                        }}
                                    >
                                        {p.ProjectTitle}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '1rem',
                                            color: '#555',
                                            lineHeight: 1.55,
                                            mb: 2,
                                        }}
                                    >
                                        {p.ProjectDescription}
                                    </Typography>
                                    <Typography
                                        sx={{ fontWeight: 700, color: 'var(--caf-text)' }}
                                    >
                                        Goal: {money(p.GoalAmount)}
                                    </Typography>
                                </CardContent>

                                <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                                    <Button
                                        className="caf-btn-red"
                                        size="large"
                                        onClick={() => {
                                            const qs = new URLSearchParams({
                                                projectId: p.apiid,
                                            }).toString();
                                            window.location.href = `${PAYMENT_PAGE}?${qs}`;
                                        }}
                                    >
                                        Donate to Project
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ░░░ Schools in Need ░░░ */}
            <Box className="caf-section" sx={{ mb: 12 }}>
                <Typography align="center" className="caf-h2">
                    Schools in Need
                </Typography>
                <Box
                    className="caf-underline"
                    sx={{ backgroundColor: 'var(--caf-blue)' }}
                />

                <Grid container spacing={4}>
                    {sites.map(s => (
                        <Grid key={s.SiteID} item xs={12} sm={6} md={4} lg={3}>
                            <Card className="caf-card" sx={{ height: '100%' }}>
                                <CardMedia
                                    component="img"
                                    height="160"
                                    image={s.LogoUrl}
                                    alt={s.SchoolName}
                                    sx={{ p: 3, objectFit: 'contain' }}
                                />

                                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography
                                        sx={{
                                            font: '600 1.25rem/1.3 "Poppins",sans-serif',
                                            color: 'var(--caf-text)',
                                            mb: 1,
                                        }}
                                    >
                                        {s.SchoolName}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.95rem', color: '#555' }}>
                                        Students in Need: <strong>{s.TotalStudents}</strong>
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.95rem', color: '#d32f2f' }}>
                                        Amount Needed:{' '}
                                        <strong>{money(s.TotalNegativeBalance)}</strong>
                                    </Typography>
                                </CardContent>

                                <CardActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
                                    <Button
                                        fullWidth
                                        className="caf-btn-blue"
                                        onClick={() => {
                                            const qs = new URLSearchParams({
                                                SchoolId: s.SiteID,
                                                amount: s.TotalNegativeBalance,
                                            }).toString();
                                            window.location.href = `${PAYMENT_PAGE}?${qs}`;
                                        }}
                                    >
                                        Help This School
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
}
